import { ListItemButton, ListItem, IconButton, Box, Card, alpha, ListItemAvatar, Avatar, ListItemText, useTheme, Grid2, Button, SxProps } from '@mui/material';
import React, { ReactNode } from 'react';
import CategoryIcon from '@mui/icons-material/Category';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink } from 'react-router-dom';

interface ListItemContainerProps {
	children: ReactNode;
	onClick?: () => void;
	selected?: boolean;
	component?: React.ElementType;
	to?: string;
	sx?: SxProps;
}

interface SimpleListAction {
	label: string;
	icon: ReactNode;
	onClick: () => void;
	sx?: SxProps;
}

interface SimpleListItemAction {
	icon: ReactNode;
	onClick?: (id?: string | number) => void;
	component?: React.ElementType;
	to?: string;
	disabled?: boolean;
}

interface SimpleListProps {
	items: SimpleListItemProps[];
	/** 
	 * @deprecated Use actions instead
	 */
	onAddClick?: () => void;
	/**
	 * @deprecated Use actions instead
	 */
	addButtonLabel?: string;
	actions?: SimpleListAction[];
	sections?: ReactNode[];
}

interface SimpleListItemPropertyText {
	label: string;
	secondary?: string | null;
}

interface SimpleListItemPropertyCustomEl {
	el: ReactNode;
}

interface SimpleListItemProps {
	id?: string | number;
	groupId?: string | number;
	label: string;
	imgUrl?: string;
	secondary?: string;
	highlightSecondary?: boolean;
	onClick?: () => void;
	component?: React.ElementType;
	to?: string;
	/**
	 * @deprecated Use actions instead
	 */
	onDeleteClick?: () => void;
	onEditClick?: () => void;
	onEnterClick?: () => void;
	actions?: SimpleListItemAction[];
	href?: string;
	properties?: (SimpleListItemPropertyText | SimpleListItemPropertyCustomEl)[];
	additionalInfo?: string | ReactNode;
	selected?: boolean;
	sx?: SxProps;
	content?: ReactNode;
}

export const ListItemContainer = ({children, onClick, selected, component, to, sx}: ListItemContainerProps) => (onClick || component && to) 
	? <ListItemButton component={component as React.ElementType} to={to as string} selected={selected} sx={{p:0, minHeight: '4rem', ...sx}} onClick={onClick}>{children}</ListItemButton> 
	: <Card sx={{borderRadius: 2, ...sx}} elevation={0}><ListItem component='div' sx={{p:0, minHeight: '4rem', backgroundColor: selected ? 'action.selected' : 'transparent'}}>{children}</ListItem></Card>;


export const SimpleList = ({items, actions}: SimpleListProps) => {
	const nonGroupedItems: SimpleListItemProps[] = [];
	const itemsByGroups = new Map<string, SimpleListItemProps[]>();
	items.forEach(item => {
		if (item.groupId) {
			itemsByGroups.set(item.groupId.toString(), [...(itemsByGroups.get(item.groupId.toString()) || []), item]);
		} else {
			nonGroupedItems.push(item);
		}
	});
	const groupedItems = nonGroupedItems.map(item => [item, ...(item.id ? itemsByGroups.get(item.id.toString()) || [] : [])]).flat();
	return <Grid2 container spacing={1} mt={1}>
		{groupedItems.map((item, i) => <Grid2 size={12} key={i}><SimpleListItem {...item} /></Grid2>)}
		<Box display='flex' alignItems='center' justifyContent='space-between'>
			{actions && actions.map((action, i) => <Button key={i} startIcon={action.icon} onClick={action.onClick} sx={action.sx}>{action.label}</Button>)}
		</Box>
	</Grid2>;
};

export const SimpleListItem = ({id, imgUrl, label, secondary, highlightSecondary, onClick, component, to, onDeleteClick, onEditClick, onEnterClick, href, properties, additionalInfo, selected, actions, sx, content}: SimpleListItemProps) => {
	const theme = useTheme();
	const shouldShowActions = onDeleteClick || onEditClick || onEnterClick;
	return <Box>
		<ListItemContainer onClick={onClick} selected={selected} component={component} to={to} sx={sx}>
			{imgUrl && <ListItemAvatar>
				<Avatar src={imgUrl} variant='square' sx={{  
					// TODO: safe to remove if hasn't caused any issues by 07.06.2025
					// 'img': {
					// objectFit: 'fit',
					// width: '70%',
					// height: '70%',
					// },
					width: 96,
					height: 96,
					borderBottomLeftRadius: 0,
					background: alpha(theme.palette.getContrastText(theme.palette.background.default), 0.12)
				}}
				>
					<CategoryIcon fontSize='large' />
				</Avatar>
			</ListItemAvatar>}
			<ListItemText
				sx={{
					...(highlightSecondary ? { '& > p': {color: 'primary.main'} } : {}),
					flex: 1,
					px: 2,
				}}
				primary={href ? <RouterLink to={href} style={{color: theme.palette.primary.main}}>{label}</RouterLink> : label}
				secondary={secondary}
			/>
			{properties && properties.length > 0 && <Box gap={2} display='flex' px={2} alignItems='center'>
				{properties.map((property, i) => (
					(property as SimpleListItemPropertyCustomEl).el ? <Box key={i}>{(property as SimpleListItemPropertyCustomEl).el}</Box> : <ListItemText
						key={i}
						sx={{
							flex: 'unset',
							'& > p': {display:'inline-block'},
						}}
						primary={(property as SimpleListItemPropertyText).label}
						secondary={(property as SimpleListItemPropertyText).secondary}
					/>
				))}
			</Box>}
			{content}
			{shouldShowActions && <Box display='flex' alignItems='center' justifyContent='space-between' m={1} gap={1}>
				{onEditClick && <IconButton onClick={onEditClick}><EditIcon /></IconButton>}
				{onEnterClick && <IconButton onClick={onEnterClick}><ChevronRightIcon /></IconButton>}
				{onDeleteClick && <IconButton onClick={onDeleteClick}><DeleteIcon /></IconButton>}
			</Box>}
			{Boolean(actions?.length) && <Box display='flex' alignItems='center' justifyContent='space-between' m={1} gap={1}>
				{actions?.map((action, i) => (
					<IconButton
						key={i}
						onClick={() => action.onClick?.(id)}
						component={action.component as React.ElementType}
						to={action.to as string}
						disabled={action.disabled}
					>
						{action.icon}
					</IconButton>
				))}
			</Box>}
		</ListItemContainer>
		{additionalInfo}
	</Box>;
};